import { IList } from 'app/services/api5-service/api.interface';

import { ISPFormState } from 'common/dynamic-form/model';

import { IListCardTermUi, IListCardUi } from '../../list-card/model';
import { spliceCells } from '../../list-card/utils/splice-cell';
import { isCellHaveDataToDisplay } from '../../list-cell/list-cell.utils';
import { IListCellUi } from '../../list-cell/model';
import { IListHeaderCellUi } from '../../list-table/model';
import { IListRowUi } from '../../model';
import { getDataForStub } from '../../utils/get-list-stub';
import { IListCardsDataUi } from '../model';

/**
 * Split row cell to sections
 *
 * @param row
 * @returns splited cells in the following order - hoverCells, headerLeftCells, headerRightCells, bodyCells, footerCells
 */
export function splitCardCells(row: IListRowUi): IListCellUi[][] {
  const allCells = row.cells.filter(isCellHaveDataToDisplay);

  spliceCells(allCells, cell => cell.type === 'empty');

  const hoverCells = spliceCells(
    allCells,
    cell => cell.type === 'hidden_button',
  );
  const headerCells = spliceCells(
    allCells,
    cell => cell.verticalAlign === 'top',
  );
  const footerCells = spliceCells(
    allCells,
    cell => cell.verticalAlign === 'bottom',
  );

  const bodyCells: IListCellUi[] = allCells;
  return [hoverCells, headerCells, bodyCells, footerCells];
}

/**
 * Get card items from rows
 *
 * @param control
 * @param rows
 * @param headerCells
 */
export function getCardItems(
  control: IList,
  rows: IListRowUi[],
  headerCells?: IListHeaderCellUi[],
): IListCardUi[] {
  const termsWidths = control.col.reduce<Record<string, number>>(
    (agg, column) => {
      const columnWidth = Number(column.$colspan);
      agg[column.$name] = columnWidth > 0 ? columnWidth : 1;
      return agg;
    },
    {},
  );

  const columnNameHeaderCells: Record<string, IListHeaderCellUi> = (
    headerCells || []
  ).reduce((reducer, headerCell) => {
    reducer[headerCell.columnName] = headerCell;
    return reducer;
  }, {});

  const mapCellsToTerms = (cells: IListCellUi[]): IListCardTermUi[] =>
    cells.map<IListCardTermUi>(cell => ({
      cell,
      label: columnNameHeaderCells[cell.columnName],
      width: termsWidths[cell.columnName],
    }));

  return rows.map(row => {
    const [hoverCells, topHeaderCells, bodyCells, footerCells] =
      splitCardCells(row);

    const card: IListCardUi = {
      key: row.key,
      keyValue: row.keyValue,
      originalElement: row.originalElement,
      headerTerms: mapCellsToTerms(topHeaderCells),
      footerTerms: mapCellsToTerms(footerCells),
      bodyTerms: mapCellsToTerms(bodyCells),
      row,
      hoverable: control.$hover !== 'no',
      hoverButtons: hoverCells.map(cell => cell.buttons || []).flat(),
    };
    return card;
  });
}

/**
 * Get list cards data
 *
 * @param control
 * @param rows
 * @param state
 * @param headerCells
 */
export function getListCardsData(
  control: IList,
  rows: IListRowUi[],
  state: ISPFormState,
  headerCells?: IListHeaderCellUi[],
): IListCardsDataUi {
  const cardsData: IListCardsDataUi = {
    type: 'cards',
    blocksInRow: control.$blocks_in_row || '2',
    items: getCardItems(control, rows, headerCells),
    maxWidth: control.$maxwidth,
    minWidth: control.$minwidth,
    stub: getDataForStub(control, state),
  };
  return cardsData;
}
